// KI-Workspace API Service
// Zentrale Verwaltung aller API-Calls für die KI-Module

const API_BASE = '/api/ki-workspace';

// Helper-Funktion für authentifizierte API-Calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('msb-token');
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

// Helper für File-Uploads
const uploadFile = async (endpoint, formData) => {
  const token = localStorage.getItem('msb-token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      // Content-Type wird automatisch für FormData gesetzt
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

// === WORKSPACE SERVICES ===
export const workspaceService = {
  // Workspace-Übersicht abrufen
  getOverview: () => {
    return apiCall('/');
  },

  // Workspace-Statistiken abrufen
  getStats: () => {
    return apiCall('/stats');
  },
};

// === CHAT SERVICES ===
export const trainingService = {
  // Lädt ein oder mehrere Dokumente hoch
  // eslint-disable-next-line camelcase
  uploadDocuments: (files, visibility = 'company', chunking_strategy = 'standard') => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('documents', file);
    });
    formData.append('visibility', visibility);
    // eslint-disable-next-line camelcase
    formData.append('chunking_strategy', chunking_strategy);
    return uploadFile('/chat/documents', formData);
  },

  // Knowledge-Dokumente abrufen
  getDocuments: () => {
    return apiCall(`/chat/documents`);
  },

  // Dokument herunterladen
  downloadDocument: async (documentId) => {
    const token = localStorage.getItem('msb-token');
    const response = await fetch(`${API_BASE}/chat/documents/${documentId}/download`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    // Dateinamen aus dem Content-Disposition-Header extrahieren
    const disposition = response.headers.get('content-disposition');
    let filename = 'downloaded-file';
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Sichtbarkeit eines Dokuments ändern
  updateDocumentVisibility: (documentId, visibility) => {
    return apiCall(`/chat/documents/${documentId}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ visibility }),
    });
  },

  // Upload-Fortschritt überwachen
  getUploadProgress: (documentId) => {
    return apiCall(`/upload-progress/${documentId}`);
  },

  getAllUploadProgress: () => {
    return apiCall('/upload-progress');
  },

  // Training starten (Platzhalter, wird derzeit nicht verwendet)
  startTraining: (documentIds) =>
    apiCall('/chat/knowledge/train', {
      method: 'POST',
      body: JSON.stringify({ documentIds }),
    }),

  // Dokument-Details abrufen
  getDocumentDetails: (documentId) => {
    return apiCall(`/chat/documents/${documentId}`);
  },

  // Dokument-Status abrufen
  getDocumentStatus: (documentId) => {
    return apiCall(`/chat/documents/${documentId}/status`);
  },

  // Dokument umbenennen
  renameDocument: (documentId, filename) => {
    return apiCall(`/chat/documents/${documentId}/rename`, {
      method: 'PUT',
      body: JSON.stringify({ filename }),
    });
  },

  // Dokument löschen
  deleteDocument: (documentId) => {
    return apiCall(`/chat/documents/${documentId}`, {
      method: 'DELETE',
    });
  },
};

export const chatService = {
  // Nachricht senden
  sendMessage: (message, conversationId = null, mode = 'documents', training = false) =>
    apiCall('/chat', {
      method: 'POST',
      body: JSON.stringify({
        message,
        conversationId,
        mode,
        training,
      }),
    }),

  // Context-Suche (Platzhalter, da die Suche jetzt Teil von sendMessage ist)
  searchContext: (query) =>
    apiCall('/chat/context/search', {
      method: 'POST',
      body: JSON.stringify({ query }),
    }),

  // Neue Funktion: Training-Flag nachträglich setzen
  setTrainingFlag: (sessionId, training) =>
    apiCall(`/chat/conversations/${sessionId}/training`, {
      method: 'PUT',
      body: JSON.stringify({
        training,
      }),
    }),
};

// === DATA ANALYSIS SERVICES ===
export const dataAnalysisService = {
  // Datensatz hochladen
  uploadDataset: (
    file,
    dataType = 'prüfwerte',
    equipmentType = 'röntgen',
    equipmentId = 'RX001',
    companyId = '1',
  ) => {
    const formData = new FormData();
    formData.append('data', file);
    formData.append('dataType', dataType);
    formData.append('equipmentType', equipmentType);
    formData.append('equipmentId', equipmentId);
    formData.append('companyId', companyId.toString());
    return uploadFile('/datenanalyse/prüfwerte/upload', formData);
  },

  // Prüfwerte extrahieren
  extractPrüfwerte: (datasetId, extractionRules = {}) =>
    apiCall('/datenanalyse/prüfwerte/extract', {
      method: 'POST',
      body: JSON.stringify({
        datasetId,
        extractionRules: {
          parameters: ['spannung_kv', 'strom_ma', 'zeit_ms', 'dosis_mgy'],
          filters: {
            dateRange: {
              from: '2024-01-01',
              to: '2024-12-31',
            },
          },
          validation: {
            checkLimits: true,
            detectAnomalies: true,
          },
          ...extractionRules,
        },
      }),
    }),

  // Trend-Analyse
  analyzeTrends: (datasetIds, analysis = {}) =>
    apiCall('/datenanalyse/trends/analyze', {
      method: 'POST',
      body: JSON.stringify({
        datasetIds,
        analysis: {
          timeframe: '6_months',
          parameters: ['spannung_kv', 'dosis_mgy'],
          groupBy: 'equipment',
          detectDrift: true,
          ...analysis,
        },
      }),
    }),

  // Vorhersage erstellen
  createPrediction: (
    datasetIds,
    predictionType = 'wartung',
    equipmentId = 'RX001',
    timeHorizon = '3_months',
    parameters = [],
  ) =>
    apiCall('/datenanalyse/predictions/create', {
      method: 'POST',
      body: JSON.stringify({
        datasetIds,
        predictionType,
        equipmentId,
        timeHorizon,
        parameters: parameters.length > 0 ? parameters : ['spannung_kv', 'dosis_mgy', 'strom_ma'],
      }),
    }),
};

// === IMAGE ANALYSIS SERVICES ===
export const imageAnalysisService = {
  // Bild für Geräte-Diagnose hochladen
  uploadImage: (
    file,
    imageType = 'gerätefoto',
    equipmentType = 'röntgen',
    equipmentId = 'RX001',
    companyId = '1',
  ) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('imageType', imageType);
    formData.append('equipmentType', equipmentType);
    formData.append('equipmentId', equipmentId);
    formData.append('companyId', companyId.toString());
    return uploadFile('/bildanalyse/geräte-diagnose/upload', formData);
  },

  // Geräte-Diagnose durchführen
  analyzeImage: (imageId, analysisType = 'defekt_erkennung', settings = {}) =>
    apiCall('/bildanalyse/geräte-diagnose/analyze', {
      method: 'POST',
      body: JSON.stringify({
        imageId,
        analysisType,
        settings: {
          confidenceThreshold: 0.8,
          detailLevel: 'hoch',
          ...settings,
        },
      }),
    }),

  // Problem-Detection-Scan
  scanProblems: (imageIds, scanType = 'batch_processing', thresholds = {}) =>
    apiCall('/bildanalyse/problem-detection/scan', {
      method: 'POST',
      body: JSON.stringify({
        imageIds,
        scanType,
        thresholds: {
          defect: 0.7,
          wear: 0.6,
          malfunction: 0.8,
          ...thresholds,
        },
      }),
    }),

  // Wartungsvorhersage
  predictMaintenance: (
    equipmentId,
    imageHistory = [],
    predictionType = 'wartungsintervall',
    timeHorizon = '6_months',
  ) =>
    apiCall('/bildanalyse/wartungsvorhersage/predict', {
      method: 'POST',
      body: JSON.stringify({
        equipmentId,
        imageHistory,
        predictionType,
        timeHorizon,
      }),
    }),
};

// === UTILITY FUNCTIONS ===
export const utils = {
  // Datei-Validierung
  validateFile: (file, allowedTypes, maxSizeMB) => {
    const errors = [];

    if (!allowedTypes.includes(file.type)) {
      errors.push(`Dateityp ${file.type} nicht unterstützt`);
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      errors.push(`Datei zu groß (max. ${maxSizeMB}MB)`);
    }

    return errors;
  },

  // Formatierung
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  },

  formatDate: (date) => {
    return new Date(date).toLocaleString('de-DE');
  },

  // Vertrauen als Prozent
  formatConfidence: (confidence) => {
    return `${Math.round(confidence * 100)}%`;
  },
};

// Default Export für einfache Verwendung
const aiService = {
  workspace: workspaceService,
  training: trainingService,
  chat: chatService,
  dataAnalysis: dataAnalysisService,
  imageAnalysis: imageAnalysisService,
  utils,
};

export default aiService;
